import React, { useContext, useEffect, useState } from 'react'
import { apiGet, apiPost, apiDelete } from '../../utils/apiFetch'
import apiPath from '../../utils/apiPath'
import OButton from 'components/reusable/OButton'
import ODateRangePicker from 'components/shared/datePicker/ODateRangePicker'
import dayjs from 'dayjs'
import Pagination from '../Pagination'
import AuthContext from 'context/AuthContext'
import { useTranslation } from 'react-i18next'
import PayrollTable from './PayrollTable'
import PayrollView from './PayrollView'
import PayrollEdit from './PayrollEdit'
import EmployeeSyncPanel from './EmployeeSyncpanel'
import TimesheetSyncPanel from './TimesheetSyncPanel'
import helpers from '../../utils/helpers'
import OSearch from 'components/reusable/OSearch'
import CustomSelect from 'components/reusable/CustomSelect'
import { FiRefreshCw, FiLoader, FiUsers, FiClock, FiDollarSign } from 'react-icons/fi'
import useToastContext from 'hooks/useToastContext'
import { useMFA } from 'context/MFAContext'
import { callWithStepUp } from 'utils/payrollMfa'

const TABS = [
  { key: 'records', label: 'Payroll Records', icon: FiDollarSign },
  { key: 'employees', label: 'Employee Sync', icon: FiUsers },
  { key: 'timesheets', label: 'Timesheet Sync', icon: FiClock }
]

const PayrollWages = () => {
  const { t } = useTranslation()
  const notification = useToastContext()
  const { requireMfa } = useMFA()
  const { updatePageName } = useContext(AuthContext)

  // ─── NEW STATE FOR SERVICE STATUS ─────────────────────────────────
  const [serviceStatus, setServiceStatus] = useState({ loading: true, isActive: false })
  
  const [activeTab, setActiveTab] = useState('records')

  // ─── Payroll records state ──────────────────────────────────────
  const [page, setPage] = useState(1)
  const [pageSize] = useState(10)
  const [paginationObj, setPaginationObj] = useState({ page: 1, pageCount: 1, pageRangeDisplayed: 10 })
  const [payrollList, setPayrollList] = useState([])
  const [currentItem, setCurrentItem] = useState('')
  const [payrollView, setPayrollView] = useState(false)
  const [payrollEdit, setPayrollEdit] = useState(false)
  const [isGenerating, setIsGenerating] = useState(false)
  const [isTriggeringPayout, setIsTriggeringPayout] = useState(false)
  const [isSyncingPayroll, setIsSyncingPayroll] = useState(false)
  const [filterData, setFilterData] = useState({
    startDate: '', endDate: '', payPeriod: '', employeeId: '', searchKey: '', isFilter: false, isReset: false
  })
  const [searchTerm, setSearchTerm] = useState('')
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState('')
  const [isInitialized, setIsInitialized] = useState(false)
  const [sort, setSort] = useState({ sortBy: 'createdAt', sortType: 'desc', sortColumn: 'payPeriod' })

  const [payPeriodOptions] = useState([
    { value: 'weekly', label: 'Weekly' },
    { value: 'bi-weekly', label: 'Bi-Weekly' },
    { value: 'monthly', label: 'Monthly' },
    { value: 'semi-monthly', label: 'Semi-Monthly' }
  ])
  const [statusOptions] = useState([
    { value: 'pending', label: 'Pending' },
    { value: 'processing', label: 'Processing' },
    { value: 'completed', label: 'Completed' },
    { value: 'failed', label: 'Failed' }
  ])

  // ─── CHECK PAYROLL SERVICE STATUS ON MOUNT ─────────────────────────
  useEffect(() => {
    const checkStatus = async () => {
      try {
        const res = await apiGet(apiPath.payrollStatus)
        if (res?.data?.success && res.data.results?.isActive) {
          setServiceStatus({ loading: false, isActive: true })
        } else {
          setServiceStatus({ loading: false, isActive: false })
        }
      } catch (error) {
        console.error('Failed to check payroll status', error)
        setServiceStatus({ loading: false, isActive: false })
      }
    }
    checkStatus()
  }, [])

  const handleView = item => { setCurrentItem(item); setPayrollView(!payrollView) }
  const handleEdit = item => { setCurrentItem(item); setPayrollEdit(true) }

  const handleDelete = async (id, confirmSyncedDelete = false) => {
    try {
      const res = await callWithStepUp(requireMfa, (extra) =>
        apiDelete(`${apiPath.payrollDelete}/${id}`, {
          ...(confirmSyncedDelete ? { confirmSyncedDelete: true } : {}),
          ...extra
        })
      )
      if (!res) {
        notification.error('Verification cancelled')
        return
      }
      if (res?.data?.success) {
        notification.success('Payroll deleted successfully')
        getPayrollList()
      } else if (res?.data?.message === 'CONFIRM_SYNCED_DELETE') {
        helpers.confirmAlertFunction(
          res.data.results?.message || 'This payroll reflects a real Everee payment. Delete anyway?',
          {},
          () => handleDelete(id, true),
          false
        )
      } else {
        notification.error(res?.data?.message || 'Failed to delete')
      }
    } catch (error) {
      notification.error('Something went wrong')
    }
  }

  const handlePageClick = event => setPage(event.selected + 1)

  const getPayrollList = async () => {
    try {
      const { searchKey, payPeriod, startDate, endDate, employeeId } = filterData
      const payload = {
        payPeriod, page, pageSize,
        startDate: helpers?.ternaryCondition(startDate, dayjs(startDate).format('YYYY-MM-DD'), null),
        endDate: helpers?.ternaryCondition(endDate, dayjs(endDate).format('YYYY-MM-DD'), null),
        keyword: searchKey?.trim(), employeeId,
        sortBy: sort.sortColumn, sortType: sort.sortType
      }
      const result = await apiGet(apiPath.payrollList, payload)
      const response = result?.data?.results
      const resultStatus = result?.data?.success
      setPayrollList(response?.docs)
      setPaginationObj({
        ...paginationObj,
        page: helpers?.ternaryCondition(resultStatus, response.page, null),
        pageCount: helpers?.ternaryCondition(resultStatus, response.totalPages, null),
        perPageItem: helpers?.ternaryCondition(resultStatus, response?.docs.length, null),
        totalItems: helpers?.ternaryCondition(resultStatus, response.totalDocs, null)
      })
    } catch (error) {
      console.log('error in get payroll list==>>>>', error.message)
    }
  }

  useEffect(() => { 
    if (serviceStatus.isActive && activeTab === 'records') {
      getPayrollList() 
    }
  }, [page, filterData, sort, activeTab, serviceStatus.isActive])

  const handleReset = () => {
    setFilterData({ searchKey: '', startDate: '', payPeriod: '', employeeId: '', endDate: '', isReset: true, isFilter: false })
    setSort({ sortBy: 'createdAt', sortType: 'desc', sortColumn: 'payPeriod' })
    setPage(1)
    setSearchTerm('')
  }

  useEffect(() => {
    helpers.setDebouncingFunction({ isInitialized, setIsInitialized, debouncedSearchTerm, searchTerm, filterData, setFilterData, setPage })
  }, [debouncedSearchTerm])

  useEffect(() => {
    const timeoutId = setTimeout(() => setDebouncedSearchTerm(searchTerm), 500)
    return () => clearTimeout(timeoutId)
  }, [searchTerm])

  useEffect(() => { updatePageName('Payroll') }, [])

  const handleDateChange = (start, end) => {
    setPage(1)
    setFilterData({ ...filterData, startDate: start, endDate: end, isFilter: true })
  }
  const handlePayPeriodChange = e => { setFilterData({ ...filterData, payPeriod: e.target.value, isFilter: true }); setPage(1) }
  const handleStatusChange = e => { setFilterData({ ...filterData, status: e.target.value, isFilter: true }); setPage(1) }
  
  const handleGeneratePayroll = async () => {
    helpers.confirmAlertFunction(
      `Generate payroll for all active employees with synced, approved timesheets?`,
      {},
      async () => {
        try {
          setIsGenerating(true)
          const res = await callWithStepUp(requireMfa, (extra) =>
            apiPost(apiPath.payrollGenerate, { ...extra })
          )
          if (!res) {
            notification.error('Verification cancelled')
            return
          }
          if (res?.data?.success) {
            const { successCount, skipped } = res.data.results || {}
            notification.success(`Generated ${successCount || 0} payroll record(s)${skipped?.length ? `, ${skipped.length} skipped — check console` : ''}`)
            if (skipped?.length) console.log('Skipped during payroll generation:', skipped)
            getPayrollList()
          } else {
            notification.error(res?.data?.message || 'Failed to generate payroll')
          }
        } catch (error) {
          notification.error('Something went wrong')
        } finally {
          setIsGenerating(false)
        }
      },
      false
    )
  }

  const handleTriggerPayout = async () => {
    helpers.confirmAlertFunction(
      `Request payout from Everee for all pending payroll records?`,
      {},
      async () => {
        try {
          setIsTriggeringPayout(true)
          const res = await callWithStepUp(requireMfa, (extra) =>
            apiPost(apiPath.payrollTriggerPayout, { ...extra })
          )
          if (!res) {
            notification.error('Verification cancelled')
            return
          }
          if (res?.data?.success) {
            const { requestedWorkers, skipped } = res.data.results || {}
            notification.success(`Payout requested for ${requestedWorkers?.length || 0} worker(s)${skipped?.length ? `, ${skipped.length} skipped` : ''}. Sync from Everee in a few minutes.`)
            if (skipped?.length) console.log('Skipped during payout trigger:', skipped)
          } else {
            notification.error(res?.data?.message || 'Failed to trigger payout')
          }
        } catch (error) {
          notification.error('Something went wrong')
        } finally {
          setIsTriggeringPayout(false)
        }
      },
      false
    )
  }

  // Pulls real gross/net/tax numbers back from Everee for records still 'processing'.
  const handleSyncPayrollFromEveree = async () => {
    try {
      setIsSyncingPayroll(true)
      const res = await apiPost(apiPath.payrollSyncFromEveree, {})
      if (res?.data?.success) {
        const { updated, stillPending } = res.data.results || {}
        notification.success(`Updated ${updated || 0} payroll record(s) from Everee${stillPending?.length ? `, ${stillPending.length} still pending` : ''}`)
        getPayrollList()
      } else {
        notification.error(res?.data?.message || 'Sync failed')
      }
    } catch (error) {
      notification.error('Something went wrong')
    } finally {
      setIsSyncingPayroll(false)
    }
  }

  // ─── RENDER LOGIC ─────────────────────────────────────────────────
  if (serviceStatus.loading) {
    return <div className="flex justify-center items-center h-[calc(100vh-100px)]"><FiLoader className="animate-spin text-4xl text-blue-500" /></div>
  }

  if (!serviceStatus.isActive) {
    return (
      <div className="bg-[#F9F9F9] h-[calc(100vh-80px)] flex items-center justify-center p-4">
        <div className="bg-white p-8 rounded-lg border border-[#E9EDF9] shadow-sm text-center max-w-md w-full">
          <div className="mx-auto w-16 h-16 bg-red-50 text-red-500 rounded-full flex items-center justify-center mb-4">
            <FiDollarSign className="w-8 h-8" />
          </div>
          <h2 className="text-xl font-bold text-gray-800 mb-2">Payroll Service Not Available</h2>
          <p className="text-gray-600 text-sm mb-6">
            The payroll service is currently inactive for your company. Please ensure your company administrator has completed the Everee onboarding and verified the API credentials.
          </p>
        </div>
      </div>
    )
  }

  return (
    <div className='bg-[#F9F9F9]'>
      <div className='py-4 px-3'>
        <div className='bg-white border rounded-lg border-[#E9EDF9]'>

          {/* ═══ TABS ═══ */}
          <div className='flex border-b border-b-[#E3E3E3] px-4'>
            {TABS.map(tab => {
              const Icon = tab.icon
              return (
                <button
                  key={tab.key}
                  type='button'
                  onClick={() => setActiveTab(tab.key)}
                  className={`flex items-center gap-2 px-4 py-3 text-sm font-medium border-b-2 transition-colors ${
                    activeTab === tab.key
                      ? 'border-[#4F46E5] text-[#4F46E5]'
                      : 'border-transparent text-gray-500 hover:text-gray-700'
                  }`}
                >
                  <Icon className='w-4 h-4' />
                  {tab.label}
                </button>
              )
            })}
          </div>

          {/* ═══ TAB: PAYROLL RECORDS ═══ */}
          {activeTab === 'records' && (
            <>
              <form className='border-b border-b-[#E3E3E3] gap-2 2xl:flex px-4 py-3'>
                <div className='col-span-2 flex flex-wrap items-center mb-2 2xl:mb-0'>
                  <div className='flex items-center flex-wrap lg:pt-0 pt-3'>
                    <div className='relative flex mb-3 items-center'>
                      <OSearch placeholder={t('SEARCH_BY_EMPLOYEE_NAME')} searchTerm={searchTerm} setSearchTerm={setSearchTerm} />
                    </div>
                    <ODateRangePicker handleDateChange={handleDateChange} setIsReset={setFilterData} isReset={filterData?.isReset} />
                    <CustomSelect id='payPeriod' name='payPeriod' options={payPeriodOptions} placeholder='Pay Period'
                      value={filterData.payPeriod} onChange={handlePayPeriodChange} t={t} extraClasses='ml-3 mb-3' />
                    <CustomSelect id='status' name='status' options={statusOptions} placeholder='Status'
                      value={filterData.status} onChange={handleStatusChange} t={t} extraClasses='ml-3 mb-3' />
                    <OButton label='Reset' extraClasses='ml-3 mb-3' style={{ borderRadius: '0.5rem' }} type='button' onClick={handleReset} />

                    <OButton
                      label={isGenerating ? 'Generating...' : 'Generate Payroll'}
                      extraClasses={`ml-3 mb-3 ${isGenerating ? 'bg-gray-400' : 'bg-emerald-500 hover:bg-emerald-600'} text-white`}
                      style={{ borderRadius: '0.5rem' }} type='button' onClick={handleGeneratePayroll} disabled={isGenerating}
                      icon={isGenerating ? <FiLoader className='animate-spin mr-2' /> : <FiRefreshCw className='mr-2' />}
                    />

                    <OButton
                      label={isTriggeringPayout ? 'Requesting...' : 'Trigger Payout'}
                      extraClasses={`ml-3 mb-3 ${isTriggeringPayout ? 'bg-gray-400' : 'bg-amber-500 hover:bg-amber-600'} text-white`}
                      style={{ borderRadius: '0.5rem' }} type='button' onClick={handleTriggerPayout} disabled={isTriggeringPayout}
                      icon={isTriggeringPayout ? <FiLoader className='animate-spin mr-2' /> : <FiRefreshCw className='mr-2' />}
                    />

                    <OButton
                      label={isSyncingPayroll ? 'Syncing...' : 'Sync from Everee'}
                      extraClasses={`ml-3 mb-3 ${isSyncingPayroll ? 'bg-gray-400' : 'bg-[#4F46E5] hover:bg-[#4338CA]'} text-white`}
                      style={{ borderRadius: '0.5rem' }} type='button' onClick={handleSyncPayrollFromEveree} disabled={isSyncingPayroll}
                      icon={isSyncingPayroll ? <FiLoader className='animate-spin mr-2' /> : <FiRefreshCw className='mr-2' />}
                    />
                  </div>
                </div>
              </form>

              {payrollView && (
                <PayrollView payrollView={payrollView} setPayrollView={setPayrollView} currentItem={currentItem} handleView={handleView} />
              )}
              {payrollEdit && (
                <PayrollEdit setPayrollEdit={setPayrollEdit} currentItem={currentItem} getPayrollList={getPayrollList} />
              )}

              <PayrollTable
                handleView={handleView}
                handleEdit={handleEdit}
                handleDelete={handleDelete}
                currentItem={currentItem}
                payrollView={payrollView}
                setSort={setSort}
                sort={sort}
                payrollList={payrollList}
                getPayrollList={getPayrollList}
                page={page}
              />

              {paginationObj?.totalItems ? (
                <Pagination page={page} handlePageClick={handlePageClick} options={paginationObj} />
              ) : null}
            </>
          )}

          {/* ═══ TAB: EMPLOYEE SYNC ═══ */}
          {activeTab === 'employees' && <EmployeeSyncPanel />}

          {/* ═══ TAB: TIMESHEET SYNC ═══ */}
          {activeTab === 'timesheets' && <TimesheetSyncPanel />}
        </div>
      </div>
    </div>
  )
}

export default PayrollWages





// import React, { useContext, useEffect, useState } from 'react'
// import { apiGet, apiPost, apiDelete } from '../../utils/apiFetch'
// import apiPath from '../../utils/apiPath'
// import OButton from 'components/reusable/OButton'
// import ODateRangePicker from 'components/shared/datePicker/ODateRangePicker'
// import dayjs from 'dayjs'
// import Pagination from '../Pagination'
// import AuthContext from 'context/AuthContext'
// import { useTranslation } from 'react-i18next'
// import PayrollTable from './PayrollTable'
// import PayrollView from './PayrollView'
// import PayrollEdit from './PayrollEdit'
// import EmployeeSyncPanel from './EmployeeSyncpanel'
// import TimesheetSyncPanel from './TimesheetSyncPanel'
// import helpers from '../../utils/helpers'
// import OSearch from 'components/reusable/OSearch'
// import CustomSelect from 'components/reusable/CustomSelect'
// import { FiRefreshCw, FiLoader, FiUsers, FiClock, FiDollarSign } from 'react-icons/fi'
// import useToastContext from 'hooks/useToastContext'
// import { useMFA } from 'context/MFAContext'
// import { callWithStepUp } from 'utils/payrollMfa'

// const TABS = [
//   { key: 'records', label: 'Payroll Records', icon: FiDollarSign },
//   { key: 'employees', label: 'Employee Sync', icon: FiUsers },
//   { key: 'timesheets', label: 'Timesheet Sync', icon: FiClock }
// ]

// const PayrollWages = () => {
//   const { t } = useTranslation()
//   const notification = useToastContext()
//   const { requireMfa } = useMFA()
//   const { updatePageName } = useContext(AuthContext)

//   const [activeTab, setActiveTab] = useState('records')

//   // ─── Payroll records state (unchanged from before) ──────────────────────
//   const [page, setPage] = useState(1)
//   const [pageSize] = useState(10)
//   const [paginationObj, setPaginationObj] = useState({ page: 1, pageCount: 1, pageRangeDisplayed: 10 })
//   const [payrollList, setPayrollList] = useState([])
//   const [currentItem, setCurrentItem] = useState('')
//   const [payrollView, setPayrollView] = useState(false)
//   const [payrollEdit, setPayrollEdit] = useState(false)
//   const [isGenerating, setIsGenerating] = useState(false)
//   const [isTriggeringPayout, setIsTriggeringPayout] = useState(false)
//   const [isSyncingPayroll, setIsSyncingPayroll] = useState(false)
//   const [filterData, setFilterData] = useState({
//     startDate: '', endDate: '', payPeriod: '', employeeId: '', searchKey: '', isFilter: false, isReset: false
//   })
//   const [searchTerm, setSearchTerm] = useState('')
//   const [debouncedSearchTerm, setDebouncedSearchTerm] = useState('')
//   const [isInitialized, setIsInitialized] = useState(false)
//   const [sort, setSort] = useState({ sortBy: 'createdAt', sortType: 'desc', sortColumn: 'payPeriod' })

//   const [payPeriodOptions] = useState([
//     { value: 'weekly', label: 'Weekly' },
//     { value: 'bi-weekly', label: 'Bi-Weekly' },
//     { value: 'monthly', label: 'Monthly' },
//     { value: 'semi-monthly', label: 'Semi-Monthly' }
//   ])
//   const [statusOptions] = useState([
//     { value: 'pending', label: 'Pending' },
//     { value: 'processing', label: 'Processing' },
//     { value: 'completed', label: 'Completed' },
//     { value: 'failed', label: 'Failed' }
//   ])

//   const handleView = item => { setCurrentItem(item); setPayrollView(!payrollView) }
//   const handleEdit = item => { setCurrentItem(item); setPayrollEdit(true) }

//     const handleDelete = async (id, confirmSyncedDelete = false) => {
//     try {
//       const res = await callWithStepUp(requireMfa, (extra) =>
//         apiDelete(`${apiPath.payrollDelete}/${id}`, {
//           ...(confirmSyncedDelete ? { confirmSyncedDelete: true } : {}),
//           ...extra
//         })
//       )
//       if (!res) {
//         notification.error('Verification cancelled')
//         return
//       }
//       if (res?.data?.success) {
//         notification.success('Payroll deleted successfully')
//         getPayrollList()
//       } else if (res?.data?.message === 'CONFIRM_SYNCED_DELETE') {
//         helpers.confirmAlertFunction(
//           res.data.results?.message || 'This payroll reflects a real Everee payment. Delete anyway?',
//           {},
//           () => handleDelete(id, true),
//           true
//         )
//       } else {
//         notification.error(res?.data?.message || 'Failed to delete')
//       }
//     } catch (error) {
//       notification.error('Something went wrong')
//     }
//   }

//   const handlePageClick = event => setPage(event.selected + 1)

//   const getPayrollList = async () => {
//     try {
//       const { searchKey, payPeriod, startDate, endDate, employeeId } = filterData
//       const payload = {
//         payPeriod, page, pageSize,
//         startDate: helpers?.ternaryCondition(startDate, dayjs(startDate).format('YYYY-MM-DD'), null),
//         endDate: helpers?.ternaryCondition(endDate, dayjs(endDate).format('YYYY-MM-DD'), null),
//         keyword: searchKey?.trim(), employeeId,
//         sortBy: sort.sortColumn, sortType: sort.sortType
//       }
//       const result = await apiGet(apiPath.payrollList, payload)
//       const response = result?.data?.results
//       const resultStatus = result?.data?.success
//       setPayrollList(response?.docs)
//       setPaginationObj({
//         ...paginationObj,
//         page: helpers?.ternaryCondition(resultStatus, response.page, null),
//         pageCount: helpers?.ternaryCondition(resultStatus, response.totalPages, null),
//         perPageItem: helpers?.ternaryCondition(resultStatus, response?.docs.length, null),
//         totalItems: helpers?.ternaryCondition(resultStatus, response.totalDocs, null)
//       })
//     } catch (error) {
//       console.log('error in get payroll list==>>>>', error.message)
//     }
//   }

//   useEffect(() => { if (activeTab === 'records') getPayrollList() }, [page, filterData, sort, activeTab])

//   const handleReset = () => {
//     setFilterData({ searchKey: '', startDate: '', payPeriod: '', employeeId: '', endDate: '', isReset: true, isFilter: false })
//     setSort({ sortBy: 'createdAt', sortType: 'desc', sortColumn: 'payPeriod' })
//     setPage(1)
//     setSearchTerm('')
//   }

//   useEffect(() => {
//     helpers.setDebouncingFunction({ isInitialized, setIsInitialized, debouncedSearchTerm, searchTerm, filterData, setFilterData, setPage })
//   }, [debouncedSearchTerm])

//   useEffect(() => {
//     const timeoutId = setTimeout(() => setDebouncedSearchTerm(searchTerm), 500)
//     return () => clearTimeout(timeoutId)
//   }, [searchTerm])

//   useEffect(() => { updatePageName('Payroll') }, [])

//   const handleDateChange = (start, end) => {
//     setPage(1)
//     setFilterData({ ...filterData, startDate: start, endDate: end, isFilter: true })
//   }
//   const handlePayPeriodChange = e => { setFilterData({ ...filterData, payPeriod: e.target.value, isFilter: true }); setPage(1) }
//   const handleStatusChange = e => { setFilterData({ ...filterData, status: e.target.value, isFilter: true }); setPage(1) }
  
//     const handleGeneratePayroll = async () => {
//     helpers.confirmAlertFunction(
//       `Generate payroll for all active employees with synced, approved timesheets?`,
//       {},
//       async () => {
//         try {
//           setIsGenerating(true)
//           const res = await callWithStepUp(requireMfa, (extra) =>
//             apiPost(apiPath.payrollGenerate, { ...extra })
//           )
//           if (!res) {
//             notification.error('Verification cancelled')
//             return
//           }
//           if (res?.data?.success) {
//             const { successCount, skipped } = res.data.results || {}
//             notification.success(`Generated ${successCount || 0} payroll record(s)${skipped?.length ? `, ${skipped.length} skipped — check console` : ''}`)
//             if (skipped?.length) console.log('Skipped during payroll generation:', skipped)
//             getPayrollList()
//           } else {
//             notification.error(res?.data?.message || 'Failed to generate payroll')
//           }
//         } catch (error) {
//           notification.error('Something went wrong')
//         } finally {
//           setIsGenerating(false)
//         }
//       },
//       true
//     )
//   }

//     const handleTriggerPayout = async () => {
//     helpers.confirmAlertFunction(
//       `Request payout from Everee for all pending payroll records?`,
//       {},
//       async () => {
//         try {
//           setIsTriggeringPayout(true)
//           const res = await callWithStepUp(requireMfa, (extra) =>
//             apiPost(apiPath.payrollTriggerPayout, { ...extra })
//           )
//           if (!res) {
//             notification.error('Verification cancelled')
//             return
//           }
//           if (res?.data?.success) {
//             const { requestedWorkers, skipped } = res.data.results || {}
//             notification.success(`Payout requested for ${requestedWorkers?.length || 0} worker(s)${skipped?.length ? `, ${skipped.length} skipped` : ''}. Sync from Everee in a few minutes.`)
//             if (skipped?.length) console.log('Skipped during payout trigger:', skipped)
//           } else {
//             notification.error(res?.data?.message || 'Failed to trigger payout')
//           }
//         } catch (error) {
//           notification.error('Something went wrong')
//         } finally {
//           setIsTriggeringPayout(false)
//         }
//       },
//       true
//     )
//   }

//   // Pulls real gross/net/tax numbers back from Everee for records still 'processing'.
//   const handleSyncPayrollFromEveree = async () => {
//     try {
//       setIsSyncingPayroll(true)
//       const res = await apiPost(apiPath.payrollSyncFromEveree, {})
//       if (res?.data?.success) {
//         const { updated, stillPending } = res.data.results || {}
//         notification.success(`Updated ${updated || 0} payroll record(s) from Everee${stillPending?.length ? `, ${stillPending.length} still pending` : ''}`)
//         getPayrollList()
//       } else {
//         notification.error(res?.data?.message || 'Sync failed')
//       }
//     } catch (error) {
//       notification.error('Something went wrong')
//     } finally {
//       setIsSyncingPayroll(false)
//     }
//   }

//   return (
//     <div className='bg-[#F9F9F9]'>
//       <div className='py-4 px-3'>
//         <div className='bg-white border rounded-lg border-[#E9EDF9]'>

//           {/* ═══ TABS ═══ */}
//           <div className='flex border-b border-b-[#E3E3E3] px-4'>
//             {TABS.map(tab => {
//               const Icon = tab.icon
//               return (
//                 <button
//                   key={tab.key}
//                   type='button'
//                   onClick={() => setActiveTab(tab.key)}
//                   className={`flex items-center gap-2 px-4 py-3 text-sm font-medium border-b-2 transition-colors ${
//                     activeTab === tab.key
//                       ? 'border-[#4F46E5] text-[#4F46E5]'
//                       : 'border-transparent text-gray-500 hover:text-gray-700'
//                   }`}
//                 >
//                   <Icon className='w-4 h-4' />
//                   {tab.label}
//                 </button>
//               )
//             })}
//           </div>

//           {/* ═══ TAB: PAYROLL RECORDS ═══ */}
//           {activeTab === 'records' && (
//             <>
//               <form className='border-b border-b-[#E3E3E3] gap-2 2xl:flex px-4 py-3'>
//                 <div className='col-span-2 flex flex-wrap items-center mb-2 2xl:mb-0'>
//                   <div className='flex items-center flex-wrap lg:pt-0 pt-3'>
//                     <div className='relative flex mb-3 items-center'>
//                       <OSearch placeholder={t('SEARCH_BY_EMPLOYEE_NAME')} searchTerm={searchTerm} setSearchTerm={setSearchTerm} />
//                     </div>
//                     <ODateRangePicker handleDateChange={handleDateChange} setIsReset={setFilterData} isReset={filterData?.isReset} />
//                     <CustomSelect id='payPeriod' name='payPeriod' options={payPeriodOptions} placeholder='Pay Period'
//                       value={filterData.payPeriod} onChange={handlePayPeriodChange} t={t} extraClasses='ml-3 mb-3' />
//                     <CustomSelect id='status' name='status' options={statusOptions} placeholder='Status'
//                       value={filterData.status} onChange={handleStatusChange} t={t} extraClasses='ml-3 mb-3' />
//                     <OButton label='Reset' extraClasses='ml-3 mb-3' style={{ borderRadius: '0.5rem' }} type='button' onClick={handleReset} />

//                     <OButton
//                       label={isGenerating ? 'Generating...' : 'Generate Payroll'}
//                       extraClasses={`ml-3 mb-3 ${isGenerating ? 'bg-gray-400' : 'bg-emerald-500 hover:bg-emerald-600'} text-white`}
//                       style={{ borderRadius: '0.5rem' }} type='button' onClick={handleGeneratePayroll} disabled={isGenerating}
//                       icon={isGenerating ? <FiLoader className='animate-spin mr-2' /> : <FiRefreshCw className='mr-2' />}
//                     />

//                     <OButton
//                       label={isTriggeringPayout ? 'Requesting...' : 'Trigger Payout'}
//                       extraClasses={`ml-3 mb-3 ${isTriggeringPayout ? 'bg-gray-400' : 'bg-amber-500 hover:bg-amber-600'} text-white`}
//                       style={{ borderRadius: '0.5rem' }} type='button' onClick={handleTriggerPayout} disabled={isTriggeringPayout}
//                       icon={isTriggeringPayout ? <FiLoader className='animate-spin mr-2' /> : <FiRefreshCw className='mr-2' />}
//                     />

//                     <OButton
//                       label={isSyncingPayroll ? 'Syncing...' : 'Sync from Everee'}
//                       extraClasses={`ml-3 mb-3 ${isSyncingPayroll ? 'bg-gray-400' : 'bg-[#4F46E5] hover:bg-[#4338CA]'} text-white`}
//                       style={{ borderRadius: '0.5rem' }} type='button' onClick={handleSyncPayrollFromEveree} disabled={isSyncingPayroll}
//                       icon={isSyncingPayroll ? <FiLoader className='animate-spin mr-2' /> : <FiRefreshCw className='mr-2' />}
//                     />
//                   </div>
//                 </div>
//               </form>

//               {payrollView && (
//                 <PayrollView payrollView={payrollView} setPayrollView={setPayrollView} currentItem={currentItem} handleView={handleView} />
//               )}
//               {payrollEdit && (
//                 <PayrollEdit setPayrollEdit={setPayrollEdit} currentItem={currentItem} getPayrollList={getPayrollList} />
//               )}

//               <PayrollTable
//                 handleView={handleView}
//                 handleEdit={handleEdit}
//                 handleDelete={handleDelete}
//                 currentItem={currentItem}
//                 payrollView={payrollView}
//                 setSort={setSort}
//                 sort={sort}
//                 payrollList={payrollList}
//                 getPayrollList={getPayrollList}
//                 page={page}
//               />

//               {paginationObj?.totalItems ? (
//                 <Pagination page={page} handlePageClick={handlePageClick} options={paginationObj} />
//               ) : null}
//             </>
//           )}

//           {/* ═══ TAB: EMPLOYEE SYNC ═══ */}
//           {activeTab === 'employees' && <EmployeeSyncPanel />}

//           {/* ═══ TAB: TIMESHEET SYNC ═══ */}
//           {activeTab === 'timesheets' && <TimesheetSyncPanel />}
//         </div>
//       </div>
//     </div>
//   )
// }

// export default PayrollWages